import {
  Box,
  Divider,
  Grid,
  HStack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { BillingMetricColumn } from "@src/components/modules/projects/detail/Stats/billing-box/components";
import type { BillingMetricColumnRow } from "@src/components/modules/projects/detail/Stats/billing-box/types";
import {
  createInvoiceRows,
  createInvoiceSegments,
  createMainSegments,
  createPrepaymentRows,
  createPrepaymentSegments,
  createProformaRows,
  createProformaSegments,
} from "@src/components/modules/projects/detail/Stats/billing-box/utils";
import { Card } from "@src/components/ui-kit";
import {
  ProgressBar,
  type TProgressBarSegment,
} from "@src/components/ui-kit/NewProgressBar";
import { ModalInvoices } from "@src/components/widgets/Modals/ModalInvoices";
import { currency } from "@src/utils/formatters";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { useStore as useAppStore } from "@src/utils/hooks/useStore";
import { observer } from "mobx-react-lite";
import { useStore } from "../../Store";
import { CardHeader } from "../CardHeader";

export const STRIPED_BG = `repeating-linear-gradient(
  -45deg,
  #b2dfdb,
  #b2dfdb 4px,
  #e0f2f1 4px,
  #e0f2f1 8px
)`;

export const BillingBoxCard = observer(function BillingBoxCard() {
  const { workspaceStore } = useAppStore();
  const store = useStore();
  const project = store.project;
  const { isMobile } = useScreenType();
  const modalInvoices = useDisclosure();

  if (!project) return null;

  const stats = project.stats;

  const handlePlannedClick = () => {
    if (!project.shouldUseNewInvoicingPlan) {
      store.billingPlanModal.onOpen();
      return;
    }

    window.open(`/projects/invoicing-plan?id=${project.id}`, "_blank");
  };

  const mainSegments: TProgressBarSegment[] = createMainSegments(stats);
  const mainSegmentInvoicedColor = mainSegments.find(
    (segment) => segment.name === "consumed",
  )?.bg;
  const mainSegmentProformaColor = mainSegments.find(
    (segment) => segment.name === "proforma",
  )?.bg;

  const invoiceSegments: TProgressBarSegment[] = createInvoiceSegments(stats);
  const invoiceRows: BillingMetricColumnRow[] = createInvoiceRows(
    project,
    workspaceStore,
    handlePlannedClick,
  );

  const proformaSegments: TProgressBarSegment[] = createProformaSegments(stats);
  const proformaRows: BillingMetricColumnRow[] = createProformaRows(
    project,
    workspaceStore,
    handlePlannedClick,
  );

  const prepaymentSegments: TProgressBarSegment[] =
    createPrepaymentSegments(stats);
  const prepaymentRows: BillingMetricColumnRow[] = createPrepaymentRows(
    stats,
    project.brand.id,
  );

  return (
    <Card p="0">
      <Box pt="6" px="6">
        <CardHeader title={<Trans>Billing</Trans>}>
          <HStack spacing="1" fontSize="md" fontWeight="bold">
            <Text color="teal.500" fontWeight="inherit">
              {currency.formatByWorkspace(stats.credit)}
            </Text>

            <Text color="gray.400" fontWeight="inherit">
              /
            </Text>

            <Text fontWeight="inherit">
              {currency.formatByWorkspace(stats.total_budget)}
            </Text>
          </HStack>
        </CardHeader>

        <Box mb="3">
          <ProgressBar size="sm" segments={mainSegments} />
        </Box>

        <HStack
          spacing={{ base: "1", md: "4" }}
          mb="3"
          fontSize="xs"
          flexDir={{ base: "column", md: "row" }}
        >
          <HStack
            spacing="2"
            justify={{ base: "space-between", md: "flex-start" }}
            w={{ base: "full", md: "unset" }}
          >
            <HStack spacing="2">
              <Box
                boxSize="2.5"
                borderRadius="lg"
                bg={mainSegmentInvoicedColor}
              />

              <Text color="gray.500">
                <Trans>Invoiced · credited to billing</Trans>
              </Text>
            </HStack>

            <Text as="span" fontWeight="semibold" color="gray.700">
              {currency.formatByWorkspace(stats.credit)}
            </Text>
          </HStack>

          <HStack
            spacing="2"
            justify={{ base: "space-between", md: "flex-start" }}
            w={{ base: "full", md: "unset" }}
          >
            <HStack spacing="2">
              <Box
                boxSize="2.5"
                borderRadius="lg"
                bg={mainSegmentProformaColor}
              />

              <Text color="gray.500">
                <Trans>Proforma · pending settlement</Trans>
              </Text>
            </HStack>

            <Text as="span" fontWeight="semibold" color="gray.700">
              {currency.formatByWorkspace(stats.unused_sum_from_proformas)}
            </Text>
          </HStack>
        </HStack>
      </Box>

      <Divider />

      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr auto) 1fr" }}
        gap="4"
        px="6"
      >
        <BillingMetricColumn
          title={<Trans>Outgoing Invoices</Trans>}
          count={project.invoiceCount}
          total={stats.outgoing_invoice_amount}
          segments={invoiceSegments}
          rows={invoiceRows}
          onHeaderClick={modalInvoices.onOpen}
        />

        {isMobile ? <Divider /> : <Divider orientation="vertical" />}

        <BillingMetricColumn
          title={<Trans>Proforma Invoices</Trans>}
          total={stats.outgoing_proforma_invoice_amount}
          count={project.proformaInvoiceCount}
          segments={proformaSegments}
          rows={proformaRows}
          dashedBefore="Settled"
          onHeaderClick={modalInvoices.onOpen}
        />

        {isMobile ? <Divider /> : <Divider orientation="vertical" />}

        <BillingMetricColumn
          title={<Trans>Prepayments</Trans>}
          count={project.prepaymentCount}
          total={stats.credit_from_prepayments}
          segments={prepaymentSegments}
          rows={prepaymentRows}
          onHeaderClick={modalInvoices.onOpen}
        />
      </Grid>

      <ModalInvoices
        {...modalInvoices}
        projectId={project.id}
        onTransfer={store.refetchProject}
      />
    </Card>
  );
});
